import { makeAutoObservable, runInAction } from 'mobx'
import { logger } from 'shared/lib'
import { isProdEnv } from 'shared/config'
import { showToast } from 'shared/ui'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { uiStore } from 'shared/store/uiStore'
import { DayjsFormats } from 'shared/lib/dayjs'
import { type IEntitiesStore } from 'entities/EntitiesExport'
import { BillingApi } from 'entities/Billing/api/billing'
import { openIntercom } from 'entities/Intercom'
import { BillingPath } from 'entities/ExportRouters'
import { type BillingPeriod } from 'pages/settings/ui/BillingPlan'
import { OpportunityModal } from 'pages/settings/pages/billing/pages/overview/ui/OpportunityModal/ui/OpportunityModal'
import { PlanInfoStore } from 'pages/settings/pages/billing/pages/overview/ui/PlanInfo/store/planInfoStore'
import { CreditsInfoStore } from 'pages/settings/pages/billing/pages/overview/ui/CreditsInfo/store/creditsInfoStore'

export class OverviewPageStore {
  private _isCancelingSubscription = false
  _planInfoStore
  private _creditsInfoStore: CreditsInfoStore

  constructor(private _entities: IEntitiesStore) {
    makeAutoObservable(this)
    this._planInfoStore = new PlanInfoStore(
      this._entities.subscriptionStore,
      this,
      this._entities.featureFlagsStore,
      this._entities.organizationStore
    )
    this._creditsInfoStore = new CreditsInfoStore(
      this._entities.subscriptionStore,
      this._entities.organizationStore,
      this._entities.billingStore
    )
  }

  openOpportunityModal = () => {
    modalStore.addModal({
      id: 'opportunityModal',
      title: 'Upgrade',
      showCloseButton: false,
      disabledOverlayClose: true,
      onClose: () => {
        modalStore.removeModal('opportunityModal')
      },
      width: '768px',
      ModalContent: OpportunityModal,
      ModalContentProps: {
        onUpgradeSuggestedPlan: this.handleUpgradeSuggestedPlan,
      },
    })
  }

  handleUpgradeSuggestedPlan = (period: BillingPeriod) => {
    modalStore.closeModal('opportunityModal')
    uiStore.changeRoute({
      path: `/${BillingPath.upgrade}`,
      options: {
        state: {
          fromSuggestion: true,
          period,
        },
      },
    })
  }

  get planInfoStore() {
    return this._planInfoStore
  }

  get onKeepPlanClick() {
    return this._planInfoStore.onKeepPlanClick
  }

  get canKeepPlan() {
    return this.isScheduledCancellation && this.subscriptionPlanPeriodEndsAt
  }

  get renewalDate() {
    return this.subscriptionPlanPeriodEndsAt
      ? uiStore.dayjs(this.subscriptionPlanPeriodEndsAt).format(DayjsFormats.full6)
      : null
  }

  get isCancelingSubscription() {
    return this._isCancelingSubscription
  }

  get subscriptionPlanPeriodEndsAt() {
    return this._entities.subscriptionStore.periodEndsAt
  }

  get isScheduledCancellation() {
    return this._entities.subscriptionStore.isScheduledCancellation
  }

  get isCustomPlanOrContract(): boolean {
    const isCustomPlan = this._entities.subscriptionStore.plan?.custom ?? false
    const isContract = this._entities.organizationStore.is_contract

    return isCustomPlan || isContract
  }

  openBuyCreditsModal = () => {
    this._creditsInfoStore.onBuyCreditsClick()
  }

  init = (checkOpportunity?: boolean) => {
    if (checkOpportunity) {
      this.openOpportunityModal()
    }
  }

  handleCancelSubscription = async () => {
    if (this._isCancelingSubscription) return

    if (this.isCustomPlanOrContract) {
      openIntercom()
      return
    }

    if (!window.churnkey) {
      logger.error('Churnkey script not loaded')
      showToast({
        title: 'Unable to process cancellation. Please contact support.',
        type: 'error',
      })
      return
    }

    this._isCancelingSubscription = true

    try {
      const { data } = await BillingApi.getChurnkeyAuth()

      window.churnkey.init('show', {
        customerId: data.customer_id,
        authHash: data.hash,
        appId: 'xiings16p',
        mode: isProdEnv ? 'live' : 'test',
        provider: 'stripe',
        onCancel: () => {
          this._entities.subscriptionStore.setIsScheduledCancellation(true)
        },
      })
    } catch (e) {
      logger.error(e)
      showToast({
        title: 'Failed to cancel subscription',
        type: 'error',
      })
    } finally {
      runInAction(() => {
        this._isCancelingSubscription = false
      })
    }
  }
}
